import React, { useState, useRef } from 'react';
import {
  Flex, Text, Box, ActionNote, StateTaxFormInfo,
} from '../../components';
import * as S from '../../styles';
import { STEPS } from '../commonStateTaxData';
import {
  step1Questions, exemptionQ3, exemptionQ4, exemptionReasonQ,
} from './data';
import FormConsumer from '../../collect-info/FormConsumers';
import StateTaxFormLayout from '../common/state-tax-content-layout';

function OhioForm({
  prefilledData = {},
  saveFormData,
  isSubmitting,
  scrollTop,
  data,
  handleSigning,
  viewResultAsManager,
  getPdfUrl,
  getInitialQuestions,
  resetInitialQuestions,
  formatFullName,
  version,
  formLink,
  isOnboardingV2,
}) {
  const [currentStep, setCurrentStep] = useState(STEPS.step1);

  prefilledData.employee_name = formatFullName();

  // versions for which 'I wish to be exempt from withholding' question is required
  const validVersions = ['12_20', '1_24'];

  const step1Form = useRef();
  const step1InitialQuestions = getInitialQuestions(step1Questions);
  const [stepOneQuestions, setStepOneQuestions] = useState(step1InitialQuestions);

  const [isExempt, setIsExempt] = useState(data.is_exempt || false);

  const exemptionQ3Form = useRef();
  const initExemptionQ3 = getInitialQuestions(exemptionQ3);
  const [exemptionQ3QnA, setExemptionQ3QnA] = useState(initExemptionQ3);

  const exemptionQ4Form = useRef();
  const initExemptionQ4 = getInitialQuestions(exemptionQ4);
  const [exemptionQ4QnA, setExemptionQ4QnA] = useState(initExemptionQ4);

  const exemptionsReasonsForm = useRef();
  const exemptionReasonInit = getInitialQuestions(exemptionReasonQ);
  const [exemptionReasonQuestion, setExemptionReasonQuestion] = useState(exemptionReasonInit);

  // If `for_yourself` is '0', then the checkbox should be checked
  const [forYourself, setForYourself] = useState(
    ['0', '', undefined].includes(data.for_yourself),
  );
  // If `for_spouse` is '0', then the checkbox should be checked
  const [forSpouse, setForSpouse] = useState(
    ['0', '', undefined].includes(data.for_spouse),
  );

  const calculateTotalExemptions = () => {
    let value = 0;
    if (!forYourself) value += 1;
    if (!forSpouse) value += 1;
    const { dependents } = exemptionQ3QnA[0].answer.answer;
    if (/^\d+$/.test(dependents)) value += parseInt(dependents);
    return value;
  };

  const renderStep1Form = (isReviewStep) => (
    <div>
      <Text fontSize="14px" fontWeight="600" mb="20px">
        Section 1: Personal information
      </Text>
      <FormConsumer
        questions={stepOneQuestions}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = stepOneQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setStepOneQuestions(questions);
        }}
        ref={step1Form}
      />
      <Text style={{ fontSize: '12px', color: 'grey' }} mb="20px">
        <span>To look up the public school district and school district number of your residence, please use </span>
        <a href="https://tax.ohio.gov/wps/portal/gov/tax/help-center/the-finder/the-finder" target="_blank" rel="noopener noreferrer">The Finder at tax.ohio.gov</a>
        .
      </Text>
    </div>
  );

  const isExemptFromWithholdingQuestion = (isReviewStep) => {
    if (validVersions.includes(version)) {
      return (
        <div>
          <Flex column>
            <S.QuestionLabel mb="7px" isRequired>
              I wish to be exempt from withholding.
            </S.QuestionLabel>
            <S.QuestionWrapper mb="20px">
              <label>
                <Flex alignItems="center">
                  <S.Input
                    type="radio"
                    value={isExempt}
                    checked={isExempt}
                    disabled={isReviewStep}
                    width="auto"
                    onChange={(e) => {
                      setIsExempt(!isExempt);
                    }}
                  />
                  <Box ml={2} />
                  Yes
                </Flex>
              </label>
              <label>
                <Flex alignItems="center">
                  <S.Input
                    type="radio"
                    value={!isExempt}
                    checked={!isExempt}
                    disabled={isReviewStep}
                    width="auto"
                    onChange={(e) => {
                      setIsExempt(!isExempt);
                    }}
                  />
                  <Box ml={2} />
                  No
                </Flex>
              </label>
            </S.QuestionWrapper>
          </Flex>
        </div>
      );
    }
  };

  const renderExemptionReasonQuestion = (isReviewStep) => {
    if (validVersions.includes(version)) {
      return (
        <div>
          <Text fontSize="14px" fontWeight="600" mb="20px">
            Section 3: Withholding Waiver
          </Text>
          <FormConsumer
            questions={exemptionReasonQuestion}
            {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
            onQuestionChange={({ question, value }) => {
              const questions = exemptionReasonQuestion.map((q) => {
                if (q.id !== question.id) return q;
                const answer = {
                  ...q.answer,
                  answer: {
                    ...q.answer.answer,
                    [q.id]: value,
                  },
                };
                const updatedQuestion = { ...q, answer };
                return updatedQuestion;
              });
              setExemptionReasonQuestion(questions);
            }}
            ref={exemptionsReasonsForm}
          />
        </div>
      );
    }
  };

  const renderExemptionQuestions = (isReviewStep) => {
    if ((!isExempt && validVersions.includes(version)) || version === '5_07') {
      return (
        <div>
          <Text fontSize="14px" fontWeight="600" mb="20px">
            Section 2: Claiming Withholding Exemptions
          </Text>
          <S.QuestionWrapper mb="15px">
            <S.QuestionLabel>
              Check the applicable boxes below:
            </S.QuestionLabel>
            <Flex mb="8px">
              <label>
                <Flex>
                  <S.Input
                    style={{ height: '22px' }}
                    type="checkbox"
                    value={forYourself}
                    onChange={(e) => setForYourself(e.target.checked)}
                    checked={forYourself}
                  />
                  <Box ml={1} />
                  You are claimed as a dependent on another individual's Ohio tax return (Line 1)
                </Flex>
              </label>
            </Flex>
            <Flex>
              <label>
                <Flex>
                  <S.Input
                    style={{ height: '22px' }}
                    type="checkbox"
                    value={forSpouse}
                    onChange={(e) => setForSpouse(e.target.checked)}
                    checked={forSpouse}
                  />
                  <Box ml={1} />
                  You are single, or have a spouse filing a separate Ohio tax return (Line 2)
                </Flex>
              </label>
            </Flex>
          </S.QuestionWrapper>
          <FormConsumer
            questions={exemptionQ3QnA}
            {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
            onQuestionChange={({ question, value }) => {
              const questions = exemptionQ3QnA.map((q) => {
                if (q.id !== question.id) return q;
                const answer = {
                  ...q.answer,
                  answer: {
                    ...q.answer.answer,
                    [q.id]: value,
                  },
                };
                const updatedQuestion = { ...q, answer };
                return updatedQuestion;
              });
              setExemptionQ3QnA(questions);
            }}
            ref={exemptionQ3Form}
          />
          <S.QuestionWrapper mb="15px">
            <Flex mb="7px">
              <S.QuestionLabel>
                Total exemptions based on the above selections (Line 4):
              </S.QuestionLabel>
            </Flex>
            <Flex>
              <S.Input
                type="text"
                value={calculateTotalExemptions()}
                disabled
              />
            </Flex>
          </S.QuestionWrapper>
          <FormConsumer
            questions={exemptionQ4QnA}
            {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
            onQuestionChange={({ question, value }) => {
              const questions = exemptionQ4QnA.map((q) => {
                if (q.id !== question.id) return q;
                const answer = {
                  ...q.answer,
                  answer: {
                    ...q.answer.answer,
                    [q.id]: value,
                  },
                };
                const updatedQuestion = { ...q, answer };
                return updatedQuestion;
              });
              setExemptionQ4QnA(questions);
            }}
            ref={exemptionQ4Form}
          />
        </div>
      );
    }
  };

  const renderStep1 = () => {
    let disabled;
    let disabledText;
    if (viewResultAsManager) {
      disabled = true;
      disabledText = 'This step has already been completed.';
    } else if (companyStaff && companyStaff.id) {
      disabled = true;
      disabledText = 'This step should be completed by the employee.';
    }

    return (
      <Flex column>
        {
          isOnboardingV2 ? null : (
            <Text fontSize="22px" bold>
              Fill state tax form
            </Text>
          )
        }
        <a href={formLink} target="_blank" rel="noreferrer">
          View original form and instructions
        </a>
        <Flex column mt={16}>
          {renderStep1Form()}
          {isExemptFromWithholdingQuestion()}
          {renderExemptionQuestions()}
          {isExempt && renderExemptionReasonQuestion()}
        </Flex>
        {viewResultAsManager && companyStaff.pii_permission === 'full_access' && (
          <Flex column mt={10} mb={10}>
            <Flex alignItems="center">
              <Text mr={16}>Download signed documents</Text>
              <a href={getPdfUrl()} target="_blank" rel="noopener noreferrer">
                <img src="/j/images/onboarding/download.svg" />
              </a>
            </Flex>
          </Flex>
        )}
        <S.Button
          disabled={disabled}
          onClick={() => {
            let err = null;
            if (step1Form && step1Form.current) {
              step1Form.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (err) return;

            if (exemptionQ3Form && exemptionQ3Form.current) {
              exemptionQ3Form.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (err) return;

            if (exemptionQ4Form && exemptionQ4Form.current) {
              exemptionQ4Form.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (err) return;

            // No error, proceed to save
            // - save answers to server
            // - processed to next step
            const stepOneData = stepOneQuestions
              .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});

            const exemptionData = { is_exempt: isExempt };
            let personalExemptionsData;
            let exemptionReasonData;
            let exemptionQ3Data;
            let exemptionQ4Data;

            if (isExempt) {
              personalExemptionsData = { for_yourself: '', for_spouse: '', total_number_of_exemptions: '' };
              exemptionQ3Data = resetInitialQuestions(exemptionQ3)
                .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
              exemptionQ4Data = resetInitialQuestions(exemptionQ4)
                .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
              exemptionReasonData = exemptionReasonQuestion
                .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
            } else {
              personalExemptionsData = { for_yourself: (forYourself ? '0' : '1'), for_spouse: (forSpouse ? '0' : '1'), total_number_of_exemptions: calculateTotalExemptions().toString() };
              exemptionQ3Data = exemptionQ3QnA
                .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
              exemptionQ4Data = exemptionQ4QnA
                .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
              exemptionReasonData = { exempt_reason: [] };
            }

            const data = {
              ...stepOneData,
              ...exemptionData,
              ...exemptionQ3Data,
              ...exemptionQ4Data,
              ...personalExemptionsData,
              ...exemptionReasonData,
            };

            saveFormData(data, () => {
              setCurrentStep(STEPS.step2);
              scrollTop();
            });
          }}
          loading={isSubmitting}
        >
          Next
        </S.Button>
        {disabledText && (
          <Flex justifyContent="flex-end">
            <ActionNote text={disabledText} />
          </Flex>
        )}
      </Flex>
    );
  };

  const renderStep2 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Review
      </Text>
      <StateTaxFormInfo />
      <a href={formLink} target="_blank" rel="noreferrer">
        View original form and instructions
      </a>
      <Flex column mt={16}>
        {renderStep1Form(true)}
        {isExemptFromWithholdingQuestion(true)}
        {renderExemptionQuestions(true)}
        {isExempt && renderExemptionReasonQuestion(true)}
      </Flex>
      <Flex mt="10px">
        <S.SecondaryButton
          disabled={viewResultAsManager}
          onClick={() => {
            setCurrentStep(STEPS.step1);
            scrollTop();
          }}
        >
          Back
        </S.SecondaryButton>
        <Flex flex={1} column ml={2}>
          <S.Button
            disabled={viewResultAsManager}
            onClick={() => {
              handleSigning();
              setCurrentStep(STEPS.step3);
              scrollTop();
            }}
            loading={isSubmitting}
          >
            Finalize and sign
          </S.Button>
        </Flex>
      </Flex>
    </Flex>
  );

  const renderStep3 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Finalize and sign
      </Text>
      <Flex column mt={44}>
        <div id="iframe-container" />
      </Flex>
    </Flex>
  );

  const isSigningStep = currentStep === STEPS.step3;

  return (
    <StateTaxFormLayout isOnboardingV2={isOnboardingV2} isSigningStep={isSigningStep}>
      {currentStep === STEPS.step1 && renderStep1()}
      {currentStep === STEPS.step2 && renderStep2()}
      {currentStep === STEPS.step3 && renderStep3()}
    </StateTaxFormLayout>
  );
}

export default OhioForm;
